<script setup lang="ts">
import { computed } from 'vue'

/**
 * A country's flag as a 16px image. A cell that shows the country's *name* beside it is
 * `GeoCell`, which draws this already.
 *
 * The image comes from flagsapi.com, not from the bundle: nothing renders until it loads,
 * and an unknown or empty code renders nothing at all rather than a broken image. The code
 * is upper-cased here, so a record's lower-case `us` is fine.
 */
const props = withDefaults(
  defineProps<{
    /** ISO 3166-1 alpha-2 (e.g. US, DE) */
    iso: string
    size?: number
  }>(),
  { size: 16 }
)

const code = computed(() =>
  String(props.iso ?? '')
    .trim()
    .toUpperCase()
)

const src = computed(() => {
  const c = code.value
  if (!c) return ''
  return `https://flagsapi.com/${c}/flat/${props.size}.png`
})
</script>

<template>
  <img
    v-if="code"
    class="flag-icon"
    :src="src"
    :width="size"
    :height="size"
    :alt="`Flag ${code}`"
    loading="lazy"
    decoding="async"
  />
</template>

<style lang="scss" scoped>
.flag-icon {
  display: inline-block;
  vertical-align: middle;
  object-fit: contain;
  flex-shrink: 0;
}
</style>
